import type { ReactNode } from "react";
import {
  Check,
  Image as ImageIcon,
  Info,
  Loader2,
  MessageSquare,
  Pencil,
  Sparkles,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn, isUuid } from "@/lib/utils";
import type { Locale } from "@/i18n/config";
import { useTranslation } from "@/i18n/use-translation";
import { useClaims, hasPermission } from "@/hooks/use-claims";
import { formatCurrency, formatDateTime } from "@/lib/format";

import { CroppedImage } from "@/components/waste/cropped-image";
import { TierBadge } from "@/components/waste/tier-badge";
import type { WasteEvent } from "@/hooks/use-waste-events";
import type { TFunction } from "@/i18n/catalog";
import { foodLabel } from "@/lib/food-labels";
import { l1ReasonAction, l1ReasonDescription } from "@/lib/l1-diagnostic-reasons";
import { serviceTimeLabel } from "@/lib/service-times";
import { wasteTypeLabel } from "@/lib/waste-types";

type Props = {
  event: WasteEvent;
  onAnalyse: (eventId: string) => void;
  onConfirm: (event: WasteEvent) => void;
  onRelabel: (event: WasteEvent) => void;
  isConfirming: boolean;
  // Resting-state presentational flags — default false when the page isn't
  // mid-analysis / the site has no DataBridge auto-classification (POS2-253).
  isAnalysing?: boolean;
  siteAutoClassifying?: boolean;
  onExpandImage: (event: WasteEvent) => void;
  siteHasActiveMenu: boolean;
  // Edit a manual log (POS2-61) — only rendered when provided.
  onEdit?: (event: WasteEvent) => void;
  // Open the comment thread for this event (POS2-760) — only rendered when provided.
  onOpenComments?: (event: WasteEvent) => void;
  // POS2-849: drop the grid-tile chrome (outer border, hover, own padding) when
  // the card is the content of another surface (the comments detail panel), so
  // it reads flat instead of a card-inside-a-card.
  bare?: boolean;
};

export function TransactionCard({
  event,
  onAnalyse,
  onConfirm,
  onRelabel,
  isConfirming,
  isAnalysing = false,
  siteAutoClassifying = false,
  onExpandImage,
  siteHasActiveMenu,
  onEdit,
  onOpenComments,
  bare = false,
}: Props) {
  const { locale, t } = useTranslation();
  // Confirm, relabel, analyse and the manual-log edit all write through `waste:update`
  // (waste_events.py), which Staff does not hold — every one of them 403'd on click
  // (POS2-1211). Reading and commenting are `waste:read` and stay.
  const { isPlatformAdmin, permissions } = useClaims();
  const canAmend = isPlatformAdmin || hasPermission(permissions, "waste:update");
  const tier = event.classification_level;
  const isUnprocessed = event.status === "processing";
  const isDiscarded = event.status === "discarded";
  const isManual = event.source === "manual";
  const canAnalyse = isUnprocessed && !!event.image_url;
  // POS2-253 — re-analyse a classified-but-unconfirmed event (the AI got it
  // wrong and staff would rather re-run than relabel by hand). Locked once
  // confirmed (backend 409 — re-analysis would overwrite the human label) and
  // never offered for manual logs or image-less events.
  const canReAnalyse =
    event.status === "successful" && !isManual && !event.is_confirmed && !!event.image_url;
  // POS2-709 — a device event whose photo hasn't synced yet. We defer
  // classification until the image lands (analysing now would short-circuit to a
  // misleading "Unidentified"), so tell staff why it isn't analysed yet instead
  // of showing a dead, disabled Analyse button.
  const awaitingImage = isUnprocessed && !isManual && !event.image_url;
  // Spec §10.4 — the action bar adapts to the tier. A one-tap Confirm only
  // appears where there is a concrete item to adopt (L4-model, menu-matched
  // L3); L1/L2 and plain L3 open the relabel picker instead. Withheld entirely
  // without `waste:update`, prompt included: an amber question the reader has no
  // way to answer is worse than no bar.
  const action = canAmend ? actionBarFor(t, event) : null;
  const weight = `${event.item_weight.toFixed(2)} kg`;
  const { primary: classificationPrimary, secondary: classificationSecondary } =
    classificationParts(t, event);
  const absoluteTime = formatAbsoluteTime(locale, event.logged_at, event.timezone);
  const hasTooltipDetail = !!event.delta_description || !!event.l1_diagnostic?.action;
  // `unassigned` reads as the em dash this card uses for every other empty field, not as the
  // word — the one place service time is displayed differently from the filter bars.
  const serviceTime =
    event.service_time && event.service_time !== "unassigned"
      ? serviceTimeLabel(t, event.service_time)
      : null;

  return (
    <article
      className={cn(
        "flex flex-col gap-3 bg-white",
        !bare &&
          "rounded-lg border border-black/[0.06] p-4 transition-colors duration-150 hover:border-black/10",
        isDiscarded && "opacity-60",
      )}
      data-testid="transaction-card"
      data-event-id={event.id}
      data-status={event.status}
      data-tier={tier ?? "none"}
    >
      {/* Image (full-width aspect 4/3). POS2-788 removed the coloured tier strip —
          tier now reads from the neutral badge below the weight. */}
      <button
        type="button"
        onClick={() => onExpandImage(event)}
        className={cn(
          "relative aspect-[4/3] w-full overflow-hidden rounded-md border border-gray-100 bg-gray-50",
          "transition-colors duration-150 hover:border-gray-200",
        )}
        disabled={!event.image_read_url && !event.before_image_read_url}
        aria-label={t("waste.expandPhoto")}
      >
        {event.image_read_url ? (
          isPlaceholderCapture(event) ? (
            // POS2-1214: the placeholder has "Waste Sensor - Weight Only" printed across it,
            // so it has to render whole — `object-cover` cropped that to "aste Sensor - Weight
            // Only". No crop box either: the box describes where *that device's camera* frames
            // its bin, and this image did not come from a camera.
            <img
              src={event.image_read_url}
              alt={t("waste.noPhotoCaptured")}
              className="size-full object-contain"
              loading="lazy"
            />
          ) : (
            <CroppedImage
              src={event.image_read_url}
              alt={t("waste.photoAlt", { id: event.id })}
              box={event.bounding_area}
              fill
              className="size-full object-cover"
              loading="lazy"
            />
          )
        ) : (
          // POS2-1214: a bare icon is indistinguishable from an image that failed to load.
          // Neutral wording on purpose — nothing here can tell a camera-less sensor from a
          // camera that dropped this frame, so "no photo" is the honest line for both.
          //
          // Except while the photo is still coming. `awaitingImage` (POS2-709) is an event
          // whose capture has not synced yet, and its notice below already says so — a tile
          // reading "No photo captured" over a line reading "Waiting for this event's photo
          // to sync" is the card contradicting itself. The icon alone is right there.
          <div className="flex size-full flex-col items-center justify-center gap-2 text-gray-400">
            <ImageIcon className="size-8 text-gray-300" aria-hidden />
            {!awaitingImage && <span className="text-xs">{t("waste.noPhotoCaptured")}</span>}
          </div>
        )}
      </button>

      {/* POS2-788: classification on the left (category on top, item below so it
          isn't truncated — spec §10.3); weight on the right with the neutral tier
          badge directly beneath it. Badge only for a real tier (L1–L4) — an
          unprocessed / discarded event has none, and its label already reads
          "Unanalysed" / "Discarded", so the right column is just the weight. */}
      <div className="flex items-start justify-between gap-2">
        <div
          className="min-w-0 flex-1"
          title={classificationTitle(t, event)}
          data-testid="classification"
        >
          <h3 className="truncate text-sm text-gray-900">{classificationPrimary}</h3>
          {classificationSecondary && (
            <p className="truncate text-sm text-gray-500">{classificationSecondary}</p>
          )}
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <span className="text-sm font-medium tabular-nums text-gray-900">{weight}</span>
          {tier != null && !isDiscarded && <TierBadge level={tier} />}
        </div>
      </div>

      {/* Metadata grid — Type / Service / Kitchen / Device (fixed position so cards align) */}
      <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs text-gray-500">
        <div className="contents">
          <dt className="text-gray-400">{t("waste.fieldType")}</dt>
          <dd className="truncate text-gray-700">
            {event.waste_type ? wasteTypeLabel(t, event.waste_type) : "—"}
          </dd>
        </div>
        <div className="contents">
          <dt className="text-gray-400">{t("waste.fieldService")}</dt>
          <dd className="truncate text-gray-700">{serviceTime ?? "—"}</dd>
        </div>
        <div className="contents">
          <dt className="text-gray-400">{t("waste.fieldKitchen")}</dt>
          <dd className="truncate text-gray-700" title={event.kitchen_name ?? ""}>
            {event.kitchen_name ?? "—"}
          </dd>
        </div>
        <div className="contents">
          <dt className="text-gray-400">{t("waste.fieldDevice")}</dt>
          <dd
            className="truncate font-mono text-[11px] text-gray-500"
            title={event.device_serial ?? ""}
          >
            {event.device_serial ?? "—"}
          </dd>
        </div>
      </dl>

      {/* Bottom block — diagnostic, action bar, footer pinned together via
          mt-auto so the amber bar sits at the same position on every card in
          a row regardless of what optional content appears above it. */}
      <div className="mt-auto flex flex-col gap-3">
        {/* POS2-709 — when we're waiting on a photo, explain that here (in the
          diagnostic slot) instead of a classification line. Otherwise show the
          L1 diagnostic / surface mismatch — inline neutral note (POS2-788). */}
        {awaitingImage ? (
          <AwaitingImageNotice autoClassifying={siteAutoClassifying} />
        ) : (
          <InlineDiagnostic event={event} />
        )}

        {/* Action bar (spec §10.2/§10.4) — appears only when the system needs
          input. Amber = a question for staff. Disappears on settled cards.
          Confirm adopts the current label; relabel opens the §10.5 picker. */}
        {action && (
          <div
            className="flex items-center justify-between gap-2 rounded-md bg-amber-50 px-2 py-1.5"
            data-testid={action.primary.kind === "confirm" ? "confirm-bar" : "relabel-bar"}
          >
            <span className="min-w-0 truncate text-xs text-amber-800">{action.prompt}</span>
            <div className="flex shrink-0 items-center gap-1.5">
              {action.secondary && (
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 text-xs text-amber-800 hover:bg-amber-100 hover:text-amber-900"
                  onClick={() => onRelabel(event)}
                  disabled={isConfirming}
                  data-testid="relabel-secondary"
                >
                  {action.secondary}
                </Button>
              )}
              {action.primary.kind === "confirm" ? (
                <Button
                  size="sm"
                  className="h-7 bg-gray-900 text-xs text-white hover:bg-gray-800"
                  onClick={() => onConfirm(event)}
                  disabled={isConfirming}
                  data-testid="confirm-button"
                >
                  {isConfirming ? t("waste.confirming") : action.primary.label}
                </Button>
              ) : (
                <Button
                  size="sm"
                  className="h-7 bg-gray-900 text-xs text-white hover:bg-gray-800"
                  onClick={() => onRelabel(event)}
                  disabled={isConfirming}
                  data-testid="relabel-button"
                >
                  {action.primary.label}
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Footer — time/cost on top, actions on a right-aligned row beneath.
            Stacking them (rather than sharing one row) keeps the footer a fixed
            height and stops the timestamp colliding with the action buttons on
            narrow cards — so the time block and the amber action bar above it
            stay aligned across every card in a row. */}
        <div className="flex flex-col gap-2 border-t border-gray-100 pt-3">
          <div className="min-w-0">
            {isUnprocessed && !siteHasActiveMenu && (
              <span className="mb-0.5 block text-[11px] text-amber-700">
                {t("waste.defaultMenuUsed")}
              </span>
            )}
            <time className="text-xs text-gray-500" dateTime={event.logged_at}>
              {absoluteTime}
              {formatCost(locale, event.cost_estimate, tier, event.currency) && (
                <span className="ml-1.5 tabular-nums text-gray-400">
                  · {formatCost(locale, event.cost_estimate, tier, event.currency)}
                </span>
              )}
            </time>
          </div>

          <div className="flex min-h-7 items-center gap-1.5">
            {onOpenComments && (
              <Button
                size="sm"
                variant="ghost"
                className={cn(
                  "h-7 gap-1 px-2 text-xs text-gray-500",
                  event.comment_count > 0 && "text-gray-700",
                )}
                onClick={() => onOpenComments(event)}
                data-testid="comments-button"
              >
                <MessageSquare className="size-3.5" aria-hidden />
                {t("waste.comments")}
                {event.comment_count > 0 && (
                  <span
                    className="rounded-full bg-gray-100 px-1.5 text-[11px] font-medium tabular-nums text-gray-600"
                    data-testid="comment-count"
                  >
                    {event.comment_count}
                  </span>
                )}
              </Button>
            )}
            {/* The remaining actions hug the right edge — ml-auto keeps them
                there even on cards without a Comments button. */}
            <div className="ml-auto flex items-center gap-1.5">
              {hasTooltipDetail && <InfoTooltip event={event} />}
              {canAmend && ((isUnprocessed && !isManual && !awaitingImage) || canReAnalyse) && (
                <AnalyseButton
                  eventId={event.id}
                  disabled={isUnprocessed && !canAnalyse}
                  loading={isAnalysing}
                  autoClassifying={siteAutoClassifying}
                  reanalyse={canReAnalyse}
                  onAnalyse={onAnalyse}
                />
              )}
              {/* Spec §10.3 — manual entries show a pencil badge (staff logged it),
                distinct from the checkmark on staff-confirmed AI classifications. */}
              {isManual ? (
                <>
                  {canAmend && onEdit && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 text-xs text-gray-500"
                      onClick={() => onEdit(event)}
                      data-testid="edit-button"
                    >
                      {t("waste.edit")}
                    </Button>
                  )}
                  <Badge className="gap-1 border-transparent bg-gray-100 text-gray-600">
                    <Pencil className="size-3" aria-hidden />
                    {t("waste.logged")}
                  </Badge>
                </>
              ) : (
                event.is_confirmed && (
                  <Badge className="gap-1 border-transparent bg-gray-100 text-gray-600">
                    <Check className="size-3" aria-hidden />
                    {t("waste.confirmed")}
                  </Badge>
                )
              )}
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────────

function AwaitingImageNotice({ autoClassifying }: { autoClassifying: boolean }) {
  const { t } = useTranslation();
  // POS2-709 — informational (amber), not an error (red): the event isn't
  // failing, it's waiting for its photo to sync before it can be analysed. Only
  // promise automatic analysis when the site is actually auto-classifying;
  // otherwise staff Analyse it manually once the photo lands.
  return (
    <p
      className="rounded-sm bg-amber-50 px-2 py-1 text-xs text-amber-800"
      data-testid="awaiting-image"
    >
      {autoClassifying ? t("waste.awaitingAuto") : t("waste.awaitingManual")}
    </p>
  );
}

function InlineDiagnostic({ event }: { event: WasteEvent }) {
  const { t } = useTranslation();
  // Surface mismatch (weight_override) takes precedence — spec §10.3 example
  if (event.weight_override && event.surface_item) {
    const weight = `${event.item_weight.toFixed(2)}kg`;
    const bulkClause =
      event.bulk_assessment === "different_identified"
        ? t("waste.bulkClassifiedSeparately")
        : t("waste.bulkUnidentified");
    return (
      <p
        className="rounded-sm bg-gray-100 px-2 py-1 text-xs text-gray-600"
        data-testid="surface-mismatch"
      >
        {t("waste.surfaceMismatch", {
          item: event.surface_item.label,
          weight,
          bulk: bulkClause,
        })}
      </p>
    );
  }

  // L1 diagnostic — just the reason (action hint moves to the info tooltip)
  if (event.classification_level === 1 && event.l1_diagnostic) {
    return (
      <p
        className="rounded-sm bg-gray-100 px-2 py-1 text-xs text-gray-600"
        data-testid="l1-diagnostic"
      >
        {l1ReasonDescription(t, event.l1_diagnostic.id, event.l1_diagnostic.description)}
      </p>
    );
  }

  return null;
}

function InfoTooltip({ event }: { event: WasteEvent }) {
  const { t } = useTranslation();
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="rounded-md p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
            aria-label={t("waste.aiDetails")}
            data-testid="info-tooltip-trigger"
          >
            <Info className="size-3.5" aria-hidden />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs space-y-1.5">
          {event.delta_description && (
            <p className="text-xs">
              <span className="font-medium">{t("waste.aiObservation")}</span>{" "}
              {event.delta_description}
            </p>
          )}
          {event.l1_diagnostic?.action && (
            <p className="text-xs italic text-gray-300">
              {/* The catalogue German for this has existed since POS2-1134 and was never rendered:
                  the card printed the API's English `action` beside a title it was already
                  translating from the same id (POS2-1267). */}
              {l1ReasonAction(t, event.l1_diagnostic.id) ?? event.l1_diagnostic.action}
            </p>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function AnalyseButton({
  eventId,
  disabled,
  loading,
  autoClassifying,
  reanalyse,
  onAnalyse,
}: {
  eventId: string;
  disabled: boolean;
  loading: boolean;
  autoClassifying: boolean;
  reanalyse: boolean;
  onAnalyse: (eventId: string) => void;
}) {
  const { t } = useTranslation();
  // The DataBridge worker only classifies `processing` events (it skips
  // already-classified ones), so auto-classification disables the first-pass
  // Analyse — the worker will get to it, manual is duplicate spend — but keeps
  // Re-analyse clickable: a classified event won't be re-run by the worker, so
  // manual re-analysis is the only AI path left for it (POS2-253).
  const blockedByAuto = autoClassifying && !reanalyse;
  const blocked = disabled || loading || blockedByAuto;
  const idle = reanalyse ? t("waste.reanalyse") : t("waste.analyse");
  const busy = reanalyse ? t("waste.reanalysing") : t("waste.analysing");
  const button = (
    <Button
      size="sm"
      variant={reanalyse ? "ghost" : "secondary"}
      className={cn("h-7 gap-1.5 text-xs", reanalyse && "text-gray-500")}
      onClick={() => {
        if (blocked) return;
        onAnalyse(eventId);
      }}
      disabled={blocked}
      aria-disabled={blocked}
      data-testid="analyse-button"
    >
      {loading ? (
        <Loader2 className="size-3 animate-spin" aria-hidden />
      ) : (
        <Sparkles className="size-3" aria-hidden />
      )}
      {loading ? busy : idle}
    </Button>
  );

  // No image — AI can't run until recheck backfills one.
  if (disabled) {
    return withTooltip(button, t("waste.noImageTooltip"));
  }
  // First-pass Analyse is disabled while the worker owns this site's queue.
  if (blockedByAuto) {
    return withTooltip(button, t("waste.autoClassifyTooltip"));
  }
  return button;
}

function withTooltip(node: ReactNode, text: string) {
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex">{node}</span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          {text}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// ── Pure helpers ────────────────────────────────────────────────────────────

function formatAbsoluteTime(locale: Locale, loggedAt: string, timezone: string | null): string {
  return formatDateTime(locale, loggedAt, {
    dateStyle: undefined,
    timeStyle: undefined,
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: timezone ?? "UTC",
    timeZoneName: "short",
  });
}

/**
 * Spec §10.3: the classification splits across two lines — the category /
 * sub-category (bold) on top, and the specific item below ("likely Item" for
 * a boosted L3 suggestion) so the item name stays visible instead of being
 * truncated into the category. `secondary` is null when there is no distinct
 * item to show.
 */
// Short, title-friendly labels for each L1 diagnostic reason, matching the
// user-guide reason names. The full sentence (`l1_diagnostic.description`) is
// too long for a card title, so the title shows the label and the description
// stays in the red detail line below. Unmapped ids fall back to "Unidentified".
// `no_visible_delta` lands via POS2-678.
const L1_REASON_LABEL_KEYS: Record<string, string> = {
  obstructed_poor_image: "waste.l1Short.obstructed_poor_image",
  image_unreadable: "waste.l1Short.image_unreadable",
  mixed_contents: "waste.l1Short.mixed_contents",
  multiple_items: "waste.l1Short.multiple_items",
  non_food_contamination: "waste.l1Short.non_food_contamination",
  insufficient_content: "waste.l1Short.insufficient_content",
  surface_mismatch: "waste.l1Short.surface_mismatch",
  before_image_unavailable: "waste.l1Short.before_image_unavailable",
  no_visible_delta: "waste.l1Short.no_visible_delta",
  bin_empty: "waste.l1Short.bin_empty",
  empty_surface: "waste.l1Short.empty_surface",
};

function classificationParts(
  t: TFunction,
  event: WasteEvent,
): {
  primary: React.ReactNode;
  secondary: React.ReactNode | null;
} {
  if (event.status === "discarded") return { primary: t("waste.discarded"), secondary: null };

  const level = event.classification_level;
  if (level === 4) {
    // The linked inventory item (staff relabel target) trumps the AI's label. Every rung goes
    // through `foodLabel`, which translates a name it knows and passes anything else through
    // as stored (POS2-1134).
    const categoryLabel = foodLabel(t, event.category_name);
    const itemLabel =
      foodLabel(t, event.inventory_item_name) ??
      foodLabel(t, event.item?.label) ??
      foodLabel(t, event.subcategory_name) ??
      categoryLabel;
    if (categoryLabel && itemLabel && itemLabel !== categoryLabel) {
      return {
        primary: <span className="font-semibold">{categoryLabel}</span>,
        secondary: itemLabel,
      };
    }
    return {
      primary: <span className="font-semibold">{itemLabel ?? t("waste.identified")}</span>,
      secondary: null,
    };
  }
  if (level === 3) {
    // POS2-628 — when consensus carries a boosted_suggestion (menu match or
    // AI-internal "2 of 3 agree on item"), show "likely Item" under the
    // deepest known label (subcategory), matching the action-bar prompt.
    if (event.boosted_suggestion && (event.subcategory_name || event.category_name)) {
      return {
        primary: (
          <span className="font-semibold">
            {foodLabel(t, event.subcategory_name) ?? foodLabel(t, event.category_name)}
          </span>
        ),
        secondary: (
          <>
            <em className="text-gray-400">{t("waste.likely")}</em>
            {foodLabel(t, event.boosted_suggestion.label)}
          </>
        ),
      };
    }
    return {
      primary: (
        <span className="font-semibold">
          {foodLabel(t, event.subcategory_name) ?? t("waste.subcategoryFallback")}
        </span>
      ),
      secondary: null,
    };
  }
  if (level === 2) {
    return {
      primary: (
        <span className="font-semibold">
          {foodLabel(t, event.category_name) ?? t("waste.categoryFallback")}
        </span>
      ),
      secondary: null,
    };
  }
  if (level === 1) {
    const reasonId = event.l1_diagnostic?.id;
    const labelKey = reasonId ? L1_REASON_LABEL_KEYS[reasonId] : undefined;
    const label = labelKey ? t(labelKey) : t("waste.eventTitleUnidentified");
    return {
      primary: <span className="font-semibold">{label}</span>,
      secondary: null,
    };
  }
  return {
    primary: <span className="text-gray-400">{t("waste.unanalysed")}</span>,
    secondary: null,
  };
}

type ActionBar = {
  prompt: string;
  primary: { kind: "confirm" | "relabel"; label: string };
  secondary: string | null;
};

/**
 * Spec §10.4 — the per-tier action bar. Returns null for settled cards
 * (confirmed, manual, discarded, still-processing). Confirm is offered only
 * where there is a concrete item to adopt (L4 with a resolved FK or adoptable
 * suggestion, menu-matched L3); every other classified state — including an
 * open-vocab L4 with no FK (POS2-962) — opens the relabel picker.
 */
/** A boosted suggestion is adoptable when it resolves to a real inventory item:
 * a menu boost carries one, and catalogue grounding (POS2-695) sets a real
 * `inventory_items` UUID. A raw AI suggestion carries an open-vocab slug and is
 * display-only ("likely X"). Shared with the confirm handler so the action bar
 * and the adopt action stay in lockstep. */
export function isAdoptableSuggestion(event: WasteEvent): boolean {
  const suggestion = event.boosted_suggestion;
  return suggestion != null && (event.menu_boosted || isUuid(suggestion.id));
}

/**
 * There is something to show, but it is not a photograph of this waste (POS2-1214).
 *
 * `_to_response` serves the shared placeholder's read URL while leaving `image_url` NULL, so
 * the two fields disagreeing *is* the signal — there is no separate flag to read. The asset
 * carries "Waste Sensor - Weight Only" printed across it, which is what makes it a labelled
 * empty state rather than a stock photo passed off as evidence.
 */
export function isPlaceholderCapture(event: WasteEvent): boolean {
  return event.image_url == null && event.image_read_url != null;
}

function actionBarFor(t: TFunction, event: WasteEvent): ActionBar | null {
  if (event.status !== "successful") return null;
  if (event.is_confirmed) return null;
  if (event.source === "manual") return null;
  const tier = event.classification_level;
  if (tier == null) return null;

  // L4 offers one-tap Confirm only with a concrete item to adopt — a resolved
  // catalogue FK or an adoptable suggestion (POS2-962, mirrors the L3 guard).
  // An open-vocab L4 with no FK (e.g. "Eggshells") falls through to relabel, so
  // it can't be confirmed into an L4 that implies a catalogue item it lacks.
  if (tier === 4 && (event.inventory_item_id != null || isAdoptableSuggestion(event))) {
    return {
      prompt: t("waste.promptLooksRight"),
      primary: { kind: "confirm", label: t("waste.actionConfirm") },
      secondary: t("waste.actionChange"),
    };
  }
  if (tier === 3 && isAdoptableSuggestion(event) && event.boosted_suggestion) {
    return {
      prompt: t("waste.promptWasThis", { item: foodLabel(t, event.boosted_suggestion.label) }),
      primary: { kind: "confirm", label: t("waste.actionConfirm") },
      secondary: t("waste.actionNotQuite"),
    };
  }
  if (tier === 1) {
    return {
      prompt: t("waste.promptWhatWasThis"),
      primary: { kind: "relabel", label: t("waste.actionAddLabel") },
      secondary: null,
    };
  }
  // L2, plain L3, and FK-less L4 — no concrete item to adopt; ask within the
  // deepest known label so the prompt matches the card title (subcategory on
  // L3/L4, which carry a resolved subcategory FK even when the item is open-vocab).
  const known =
    (tier >= 3 ? foodLabel(t, event.subcategory_name) : undefined) ??
    foodLabel(t, event.category_name);
  return {
    prompt: known ? t("waste.promptWhatType", { known }) : t("waste.promptWhatWasThis"),
    primary: { kind: "relabel", label: t("waste.actionAddDetail") },
    secondary: null,
  };
}

function classificationTitle(t: TFunction, event: WasteEvent): string {
  const parts: string[] = [];
  const categoryLabel = foodLabel(t, event.category_name);
  if (categoryLabel) parts.push(categoryLabel);
  const child =
    foodLabel(t, event.inventory_item_name) ??
    foodLabel(t, event.item?.label) ??
    foodLabel(t, event.subcategory_name);
  if (child && child !== categoryLabel) parts.push(child);
  return parts.join(" — ") || t("waste.unanalysed");
}

function formatCost(
  locale: Locale,
  cost: number | null | undefined,
  tier: number | null | undefined,
  currency: string,
): string | null {
  if (cost == null) return null;
  const value = formatCurrency(locale, cost, currency);
  if (tier === 4) return value;
  if (tier === 2) return `${value} avg`;
  if (tier === 3 || tier === 1) return `${value} est.`;
  return value;
}
